/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState, useMemo } from 'react';
import { fetchRecentMetadata, TokenMetadata } from '../lib/pinata';
import { generateRankings } from '../lib/ranking';
import { RankedToken } from '../types/ranking';
import { 
    TrendingUp, Search, Trophy, ArrowRight, X, Flame, AlertTriangle, Brain, Star, Tag
} from 'lucide-react';
import { TradeModal } from './TradeModal';
import { TokenDetailsModal } from './TokenDetailsModal';
import { formatCompact, formatAddress } from '../lib/utils/format';
import { useBondingCurve } from '../hooks/use-bonding-curve';
import { useNetwork } from '../hooks/use-network';
import { useWallet } from '../providers/WalletProvider';
import { useAiContext } from '../providers/ai-provider';
import { useWatchlist } from '../context/WatchlistContext';
import { AiSignal } from '../types/ai';

const TokenSkeleton = () => (
    <div className="bg-white/60 backdrop-blur-sm p-5 rounded-2xl border border-stone-200 shadow-soft-lg h-[340px] flex flex-col animate-pulse">
        <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-stone-200 flex-shrink-0"></div>
            <div className="flex-1 space-y-2">
                <div className="h-4 bg-stone-200 rounded-lg w-3/4"></div>
                <div className="h-3 bg-stone-200 rounded-lg w-1/2"></div>
            </div>
        </div>
        <div className="h-12 bg-stone-100 rounded-xl mb-4 w-full"></div>
        <div className="space-y-3 mt-auto">
             <div className="flex justify-between">
                 <div className="h-3 bg-stone-200 rounded-lg w-16"></div>
                 <div className="h-3 bg-stone-200 rounded-lg w-12"></div>
             </div>
             <div className="h-2 bg-stone-200 rounded-full w-full"></div>
             <div className="h-10 bg-stone-200 rounded-xl w-full mt-2"></div>
        </div>
    </div>
);

interface TokenCardProps {
    ranked: RankedToken;
    onClick: () => void;
    isDevnet: boolean;
    aiSignal?: AiSignal | null;
}

const TokenCard: React.FC<TokenCardProps> = ({ ranked, onClick, isDevnet, aiSignal }) => {
    const curve = useBondingCurve(ranked.token.properties?.mintAddress);
    const { isFavorited, addToWatchlist, removeFromWatchlist } = useWatchlist();
    const mintAddress = ranked.token.properties?.mintAddress;
    const isFavorite = mintAddress ? isFavorited(mintAddress) : false;
    
    // Fallback logic for progress if curve data is loading or missing
    const progress = curve ? curve.progress : Math.min(((ranked.metrics.marketCap / 20000) * 100), 100);
    const mcap = curve ? curve.marketCap : ranked.metrics.marketCap;
    const isGraduated = curve?.isGraduated || progress >= 100;
    const isSui = ranked.token.properties?.chain === 'SUI';
    const isTrending = ranked.score > 700;

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!isDevnet) onClick();
        }
    };

    const handleFavoriteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!mintAddress) return;
        if (isFavorite) {
            removeFromWatchlist(mintAddress);
        } else {
            addToWatchlist(mintAddress);
        }
    };

    return (
        <div 
            className={`
                effect-frame relative flex flex-col h-full p-5 rounded-2xl border transition-all duration-300 group overflow-hidden bg-white
                ${isGraduated 
                    ? 'border-amber-200 shadow-amber-50 hover:shadow-amber-100 hover:border-amber-400' 
                    : isTrending 
                        ? 'border-stone-200 shadow-soft-lg hover:shadow-soft-xl hover:border-stone-300'
                        : 'border-stone-200 shadow-soft-lg hover:border-stone-300 hover:shadow-soft-xl'
                }
            `}
            role="button"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onClick={!isDevnet ? onClick : undefined}
            style={{ cursor: isDevnet ? 'default' : 'pointer' }}
        >
            {/* Background elements */}
            {ranked.token.header_image && (
                <div className="absolute top-0 left-0 w-full h-32 overflow-hidden z-0 rounded-t-xl opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                    <img src={ranked.token.header_image} alt="" className="w-full h-full object-cover blur-sm" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white"></div>
                </div>
            )}

            <div className="relative z-10 flex flex-col h-full">
                {/* Live Indicator & Badges */}
                <div className="absolute top-0 right-0 flex items-center gap-1.5 z-20 max-w-[80%] justify-end flex-wrap">
                    <span className="font-serif font-bold text-lg text-stone-300 group-hover:text-stone-400 transition-colors mr-1">
                        #{ranked.rank}
                    </span>
                    
                    {aiSignal && !isGraduated && (
                         <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border whitespace-nowrap shadow-sm
                            ${aiSignal.type === 'BUY' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}
                         `}>
                            <Brain size={12} /> AI {aiSignal.type}
                        </span>
                    )}
                    {isTrending && !isGraduated && (
                        <span className="flex items-center gap-1 bg-gradient-to-r from-amber-300 via-yellow-500 to-amber-600 text-white px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border border-white/20 shadow-sm whitespace-nowrap">
                            <Flame size={12} className="fill-white" /> Hot
                        </span>
                    )}
                    {isGraduated && (
                        <span className="flex items-center gap-1 bg-gradient-to-r from-amber-300 via-yellow-500 to-amber-600 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border border-white/20 shadow-sm whitespace-nowrap">
                            <Flame size={12} className="fill-white" /> HOT
                        </span>
                    )}
                    {!isGraduated && !isTrending && (
                        <span className="relative flex h-2 w-2 ml-1 shrink-0">
                          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-green-500`}></span>
                          <span className={`relative inline-flex rounded-full h-2 w-2 bg-green-500`}></span>
                        </span>
                    )}
                </div>

                {/* Header Section */}
                <div className="flex items-start gap-4 mb-3 relative pt-6">
                    <div className="relative w-14 h-14 rounded-2xl bg-stone-50 shrink-0 border border-stone-200 shadow-md group-hover:scale-105 transition-transform duration-300">
                        {ranked.token.image ? (
                            <img src={ranked.token.image} alt="" className="w-full h-full object-cover rounded-2xl" loading="lazy" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-stone-400 font-bold text-lg rounded-2xl">
                                {ranked.token.symbol ? ranked.token.symbol[0] : '?'}
                            </div>
                        )}
                        {/* Chain Icon on Corner of Logo */}
                        <div className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-md border border-stone-100 z-10 p-0.5">
                            <img 
                                src={isSui ? 'https://cryptologos.cc/logos/sui-sui-logo.png' : 'https://cryptologos.cc/logos/solana-sol-logo.png'} 
                                className="w-full h-full object-contain" 
                                alt={isSui ? "Sui" : "Solana"}
                            />
                        </div>
                    </div>
                    
                    <div className="flex-1 min-w-0 pt-0.5 pr-2">
                        <div className="flex items-center gap-2">
                            <h4 className="font-serif font-bold text-lg text-stone-900 truncate leading-tight group-hover:text-nobel-gold transition-colors">
                                {ranked.token.name}
                            </h4>
                            <button
                                onClick={handleFavoriteClick}
                                className="p-1 text-stone-300 hover:text-amber-500 transition-colors rounded-full shrink-0"
                                title={isFavorite ? "Remove from Watchlist" : "Add to Watchlist"}
                            >
                                <Star size={14} className={`transition-all duration-300 ${isFavorite ? 'fill-amber-500 text-amber-500' : 'fill-transparent stroke-current'}`} />
                            </button>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-[10px] font-mono font-bold text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full border border-stone-200 uppercase truncate max-w-[100px]">
                                ${ranked.token.symbol}
                            </span>
                            {ranked.token.properties?.extensions?.interestRate ? (
                                <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100 flex items-center gap-0.5 whitespace-nowrap">
                                    <TrendingUp size={8} /> Yield
                                </span>
                            ) : null}
                        </div>
                    </div>
                </div>
                
                {/* Description */}
                <p className="text-xs text-stone-500 line-clamp-2 mb-3 h-[2.5em] leading-relaxed">
                    {ranked.token.description || "No description provided for this project."}
                </p>

                {/* Utility Tags */}
                <div className="flex flex-wrap gap-1 mb-4 h-6 overflow-hidden">
                    {ranked.token.tags && ranked.token.tags.length > 0 ? (
                        ranked.token.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="text-[9px] font-bold text-stone-500 bg-stone-50 border border-stone-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Tag size={8} /> {tag}
                            </span>
                        ))
                    ) : (
                        <span className="text-[9px] font-bold text-stone-400 bg-stone-50 border border-stone-100 px-2 py-0.5 rounded-full">
                            New Launch
                        </span>
                    )}
                </div>
                
                {/* Metrics */}
                <div className="mt-auto space-y-3">
                    <div className="flex items-end justify-between">
                        <div>
                            <div className="text-[9px] font-bold text-stone-400 uppercase tracking-wide mb-0.5">Market Cap</div>
                            <div className="font-mono text-sm font-bold text-stone-900">${formatCompact(mcap)}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[9px] font-bold text-stone-400 uppercase tracking-wide mb-0.5">Bonding Curve</div>
                            <div className={`text-sm font-bold ${isGraduated ? 'text-amber-600' : 'text-stone-600'}`}>
                                {progress.toFixed(0)}%
                            </div>
                        </div>
                    </div>
                    
                    <div className="h-1.5 w-full bg-stone-100 rounded-full overflow-hidden">
                        <div 
                            className={`h-full rounded-full transition-all duration-1000 ease-out ${isGraduated ? 'bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500' : 'bg-gradient-to-r from-stone-300 to-stone-400'}`}
                            style={{ width: `${Math.min(progress, 100)}%` }}
                        ></div>
                    </div>
                </div>

                {/* Action Button - Always Visible */}
                <div className="pt-4 mt-4 border-t border-stone-100">
                    {isDevnet ? (
                        <button className="w-full py-2.5 bg-stone-100 text-stone-400 rounded-xl font-bold text-xs flex items-center justify-center gap-2 cursor-not-allowed border border-stone-200">
                            <AlertTriangle size={14} /> Devnet Mode
                        </button>
                    ) : (
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                onClick();
                            }}
                            className="w-full py-2.5 bg-stone-900 text-white rounded-xl font-bold text-xs shadow-md flex items-center justify-center gap-2 hover:bg-nobel-gold hover:text-stone-900 transition-colors group/btn"
                        >
                            Details & Trade <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export const RunningTokens: React.FC = () => {
    const [rankedTokens, setRankedTokens] = useState<RankedToken[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedToken, setSelectedToken] = useState<RankedToken | null>(null);
    const [isTradeOpen, setIsTradeOpen] = useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    
    // Global Context
    const { currentCluster } = useNetwork();
    const { chain } = useWallet();
    const { signals: aiSignals } = useAiContext();
    const { watchlist } = useWatchlist();
    
    // Search and Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState<'ALL' | 'SOL' | 'SUI' | 'WATCHLIST'>('ALL');

    const PAGE_LIMIT = 24;
    const isDevnet = currentCluster.network === 'devnet';

    // Auto-sync chain filter with global chain selection
    useEffect(() => {
        if (chain) {
            setFilter(chain as 'SOL' | 'SUI');
        }
    }, [chain]);

    const loadTokens = async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch more tokens to ensure we have a good pool for ranking
            // IMPORTANT: Pass current network status to fetcher
            const { tokens: newTokens } = await fetchRecentMetadata(0, 50, !isDevnet); 
            const newRankedBatch = generateRankings(newTokens);
            
            // Sort by Rank/Score explicitly
            newRankedBatch.sort((a, b) => b.score - a.score);
            
            setRankedTokens(newRankedBatch);
        } catch (error) {
            console.error("Failed to load tokens", error);
            setError("Unable to load token data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTokens();
        // Listen for new mints to refresh the list
        const handleNewToken = () => loadTokens();
        window.addEventListener('hot_tokens_updated', handleNewToken);
        return () => window.removeEventListener('hot_tokens_updated', handleNewToken);
    }, [isDevnet]); 

    const filteredTokens = useMemo(() => {
        return rankedTokens.filter(ranked => {
            const token = ranked.token;
            const matchesSearch = 
                (token.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
                (token.symbol?.toLowerCase() || '').includes(searchTerm.toLowerCase());
            
            if (filter === 'WATCHLIST') {
                return matchesSearch && token.properties?.mintAddress && watchlist.includes(token.properties.mintAddress);
            }
            
            const matchesChain = filter === 'ALL' || token.properties?.chain === filter;
            
            return matchesSearch && matchesChain;
        }).slice(0, PAGE_LIMIT);
    }, [rankedTokens, searchTerm, filter, watchlist]);

    return (
        <div className="w-full flex flex-col items-center max-w-full">
             
             {/* Unified Search and Filters */}
             <div className="w-full mb-8 relative z-20">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="bg-white p-1.5 rounded-lg border border-stone-200 shadow-soft-md flex items-center gap-2 focus-within:border-nobel-gold focus-within:ring-2 focus-within:ring-nobel-gold/10 transition-all w-full md:w-auto">
                        <Search className="text-stone-400 ml-2" size={18} />
                        <input 
                            type="text" 
                            placeholder="Find a gem..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full md:w-64 pl-2 pr-4 py-2 bg-transparent outline-none text-sm font-medium text-stone-900 placeholder:text-stone-400"
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} className="p-1 hover:bg-stone-100 rounded-md text-stone-400">
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-stone-200 shadow-soft-md">
                        {(['ALL', 'SOL', 'SUI', 'WATCHLIST'] as const).map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-4 py-2 rounded-md text-xs font-bold transition-all flex items-center gap-1.5 ${
                                    filter === f 
                                    ? 'bg-stone-900 text-white shadow-md' 
                                    : 'text-stone-500 hover:text-stone-900 hover:bg-stone-50'
                                }`}
                            >
                                {f === 'WATCHLIST' && <Star size={12} className={filter === 'WATCHLIST' ? 'fill-current' : ''} />}
                                {f === 'ALL' ? 'All Chains' : f === 'WATCHLIST' ? 'Favorites' : f}
                            </button>
                        ))}
                    </div>
                </div>
             </div>

             {/* Trade Modal Integration */}
             {selectedToken && (
                 <TradeModal 
                    token={selectedToken.token}
                    currentPrice={0} // Will be fetched inside modal or updated
                    isOpen={isTradeOpen}
                    onClose={() => setIsTradeOpen(false)}
                    initialSide="BUY"
                 />
             )}

             {/* Details Modal Integration */}
             {selectedToken && (
                 <TokenDetailsModal 
                    token={selectedToken.token}
                    isOpen={isDetailsOpen}
                    onClose={() => setIsDetailsOpen(false)}
                    onTrade={() => {
                        setIsDetailsOpen(false);
                        setIsTradeOpen(true);
                    }}
                    price={0}
                 />
             )}

             {/* Main Grid */}
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full auto-rows-fr">
                {error ? (
                    <div className="col-span-full text-center py-24 flex flex-col items-center justify-center bg-stone-50 rounded-2xl border border-stone-200 border-dashed">
                        <Trophy className="text-stone-300 mb-4" size={48} />
                        <p className="text-stone-500 mb-6 font-medium">{error}</p>
                        <button onClick={loadTokens} className="px-6 py-2.5 bg-white border border-stone-300 rounded-lg font-bold hover:border-stone-900 transition-colors shadow-sm">Retry Connection</button>
                    </div>
                ) : loading ? (
                    [...Array(8)].map((_, i) => <TokenSkeleton key={i} />)
                ) : (
                    <>
                        {filteredTokens.length === 0 && (
                            <div className="col-span-full text-center py-24 bg-stone-50 rounded-2xl border border-stone-200 border-dashed">
                                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                                    {filter === 'WATCHLIST' ? <Star size={24} className="text-stone-300" /> : <Search size={24} className="text-stone-300" />}
                                </div>
                                <h3 className="text-lg font-bold text-stone-900 mb-1">No markets found</h3>
                                <p className="text-stone-500 text-sm">{filter === 'WATCHLIST' ? "Your watchlist is empty." : "Try adjusting your search filters."}</p>
                            </div>
                        )}

                        {filteredTokens.map((ranked, idx) => {
                            const signal = aiSignals.find(s => s.token.properties?.mintAddress === ranked.token.properties?.mintAddress);
                            return (
                                <TokenCard 
                                    key={`${ranked.token.symbol}-${idx}`} 
                                    ranked={ranked} 
                                    onClick={() => { setSelectedToken(ranked); setIsDetailsOpen(true); }}
                                    isDevnet={isDevnet}
                                    aiSignal={signal}
                                />
                            );
                        })}
                    </>
                )}
             </div>
        </div>
    );
};